import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import concat from 'lodash/concat';
import moment from 'moment';

import Container from '../container/container';
import ContentFilter from '../content-filter/content-filter';
import { listingFilter, removeEmptyListings } from './content-list-utls';
import ContentTeaser from '../content-teaser/content-teaser';
import Icon from '../icons/icon';

import styles from './content-list.module.scss';
import { filterExpiredNodes, get } from '../../helpers/hooks-helpers';

class ContentList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      listings: this.getDefaultListings(),
      filtered: false,
      loading: false,
      selectedFilters: null,
      selectedDates: null
    };

    this.updateContent = this.updateContent.bind(this);
    this.applyFilters = this.applyFilters.bind(this);
    this.sortDates = this.sortDates.bind(this);
    this.sortDiningListings = this.sortDiningListings.bind(this);
    this.resetResults = this.resetResults.bind(this);
  }

  getDefaultListings() {
    const listings = get(this.props, 'data', []).filter(filterExpiredNodes);

    if (listings.length && listings[0]?.node?.__typename === 'node__dining') {
      return this.sortDiningListings(listings);
    }

    return listings;
  }

  sortDiningListings(listings) {
    const distanceOrder = {
      'on site': 0,
      'less than 5 minute walk': 1,
      '5-10 minute walk': 2
    };

    const normalizeDistance = (value = '') => {
      return value.toLowerCase().trim().replace(/\s+/g, ' ');
    };

    return [...listings].sort((a, b) => {
      const distanceA = normalizeDistance(
        a.node?.relationships?.field_distance?.metatag?.[0]?.attributes?.content
      );
      const distanceB = normalizeDistance(
        b.node?.relationships?.field_distance?.metatag?.[0]?.attributes?.content
      );

      const rankA = distanceOrder[distanceA] ?? 999;
      const rankB = distanceOrder[distanceB] ?? 999;

      if (rankA !== rankB) {
        return rankA - rankB;
      }

      const titleA = a.node?.title || '';
      const titleB = b.node?.title || '';

      return titleA.localeCompare(titleB);
    });
  }

  updateContent({ selectedFilters, selectedDates }) {
    // Pull in the default listings.
    const listings = this.getDefaultListings();

    this.setState({ loading: true });

    // Set results defaults.
    let results = {
      listings: listings,
      filtered: false
    };

    // Update the state with the current filters or dates.
    if (!selectedFilters) {
      selectedFilters = this.state.selectedFilters;
    }
    if (!selectedDates) {
      selectedDates = this.state.selectedDates;
    }

    // Pass our listings through our filters.
    results = selectedFilters
      ? this.applyFilters(selectedFilters, listings)
      : results;
    results = selectedDates ? this.sortDates(selectedDates, results) : results;

    // Sort dining listings by distance, then alphabetically by title.
    if (
      results.listings.length &&
      results.listings[0]?.node?.__typename === 'node__dining'
    ) {
      results.listings = this.sortDiningListings(results.listings);
    }

    // Update filtered listings and if results are filtered or not.
    setTimeout(() => {
      this.setState({
        listings: results.listings,
        filtered: results.filtered,
        selectedFilters,
        selectedDates,
        loading: false
      });
    }, 500);
  }

  applyFilters(selectedFilters, listings) {
  // Check to see if any tags filters are active.
    const tagsActiveFilters = Object.keys(selectedFilters.field_tags).filter(
      (key) => {
        return selectedFilters.field_tags[key];
      }
    );

    // Check to see if any series filters are active.
    const seriesActiveFilters = Object.keys(
      selectedFilters.field_series
    ).filter((key) => {
      return selectedFilters.field_series[key];
    });

    // Check to see if any venue filters are active.
    const venueActiveFilters = Object.keys(selectedFilters.field_venue).filter(
      (key) => {
        return selectedFilters.field_venue[key];
      }
    );

    // Check to see if any presented by filters are active.
    const presetedByActiveFilters = Object.keys(
      selectedFilters.field_presented_by
    ).filter((key) => {
      return selectedFilters.field_presented_by[key];
    });

    // Check to see if any distance filters are active.
    const distanceActiveFilters = Object.keys(
      selectedFilters.field_distance || {}
    ).filter((key) => {
      return selectedFilters.field_distance[key];
    });

    // Check to see if any experience filters are active.
    const experienceActiveFilters = Object.keys(
      selectedFilters.field_experience || {}
    ).filter((key) => {
      return selectedFilters.field_experience[key];
    });

    // Check to see if any cuisine filters are active.
    const cuisineActiveFilters = Object.keys(
      selectedFilters.field_cuisine || {}
    ).filter((key) => {
      return selectedFilters.field_cuisine[key];
    });

    // Build an array of all available filters so we can check if any
    // are selected.
    const hasSelectedFilters = concat(
      tagsActiveFilters,
      seriesActiveFilters,
      venueActiveFilters,
      presetedByActiveFilters,
      distanceActiveFilters,
      experienceActiveFilters,
      cuisineActiveFilters
    );

    // Make sure filters are selected before trying to filter.
    if (hasSelectedFilters.length) {
    // Remove listings that contain nothing in the following fields:
    // ['field_tags', 'field_series', 'field_venue', 'field_presented_by',
    //  'field_distance', 'field_experience', 'field_cuisine'].
    // We don't want them to show up under _any_ filtered results.
      let newFilteredlistings = removeEmptyListings({
        listings,
        fields: [
          'field_tags',
          'field_series',
          'field_venue',
          'field_presented_by',
          'field_distance',
          'field_experience',
          'field_cuisine'
        ]
      });

      // Find all listings that contain one of the tags.
      newFilteredlistings = listingFilter({
        listings: newFilteredlistings,
        selectedFilters: tagsActiveFilters,
        field: 'field_tags'
      });

      // Filter those listings to exclude any without the right series.
      newFilteredlistings = listingFilter({
        listings: newFilteredlistings,
        selectedFilters: seriesActiveFilters,
        field: 'field_series'
      });

      // Filter those listings to exclude any without the right venues.
      newFilteredlistings = listingFilter({
        listings: newFilteredlistings,
        selectedFilters: venueActiveFilters,
        field: 'field_venue'
      });

      // Filter those listings to exclude any without the right presenter.
      newFilteredlistings = listingFilter({
        listings: newFilteredlistings,
        selectedFilters: presetedByActiveFilters,
        field: 'field_presented_by'
      });

      // Filter those listings to exclude any without the right distance.
      newFilteredlistings = listingFilter({
        listings: newFilteredlistings,
        selectedFilters: distanceActiveFilters,
        field: 'field_distance'
      });

      // Filter those listings to exclude any without the right experience.
      newFilteredlistings = listingFilter({
        listings: newFilteredlistings,
        selectedFilters: experienceActiveFilters,
        field: 'field_experience'
      });

      // Filter those listings to exclude any without the right cuisine.
      newFilteredlistings = listingFilter({
        listings: newFilteredlistings,
        selectedFilters: cuisineActiveFilters,
        field: 'field_cuisine'
      });

      // Return our new filtered listings.
      return { listings: newFilteredlistings, filtered: true };
    }
    else {
    // No filters are applied so return the original listings.
      return { listings: listings, filtered: false };
    }
  }

  resetResults() {
    this.setState({ loading: true });

    setTimeout(() => {
      this.setState({
        listings: this.getDefaultListings(),
        filtered: false,
        selectedFilters: null,
        selectedDates: null,
        loading: false
      });
    }, 500);
  }

  sortDates(selectedDates, { listings }) {
    // If there are no selected dates return the original listings.
    if (selectedDates.startDate && selectedDates.endDate) {
      const filteredListings = listings.filter((listing) => {
        // If there is no start date don't show this event.
        if (!listing.node.date) {
          return false;
        }

        // This is difficult to understand so here are
        // some examples that will hopefully help.
        //
        // A range of dates is selected:
        // i.e.
        // 08-1 -> 08-3
        // This event has a start date of 08-02.
        // This event HAS started because the event start
        // date 08-02 is the SAME or BEFORE the selected
        // range end date 08-03.
        const eventHasStarted = moment(
          `${listing.node.date.start} 12:00:00`
        ).isSameOrBefore(selectedDates.endDate);

        // A range of dates is selected:
        // i.e.
        // 08-1 -> 08-3
        // This event has a end date of 07-10.
        // This event HAS ended because the event end
        // date 07-10 is BEFORE the selected
        // range start date 08-01.
        const eventHasEnded = moment(
          `${listing.node.date.end} 12:00:00`
        ).isBefore(selectedDates.startDate);

        // Make sure the event has started AND that it hasn't ended yet.
        return eventHasStarted && !eventHasEnded;
      });

      return { listings: filteredListings, filtered: true };
    }
    else {
      return { listings: listings, filtered: false };
    }
  }

  render() {
    const { data, filters, color, children } = this.props;

    const listClasses = classnames(styles.contentList, {
      [styles.backgroundBlue]: color === 'blue'
    });

    const contentClasses = classnames({
      [styles.backgroundBlue]: color === 'blue'
    });

    return (
      <div>
        {filters && (
          <ContentFilter
            desc={`Filter ${get(data, '[0].node.relationships.node_type.name', 'result')}s`}
            filters={filters}
            updateContent={this.updateContent}
            resetResults={this.resetResults}
            isFiltered={this.state.filtered}
            allEvents={data}
            currentEvents={this.getDefaultListings()}
            // eslint-disable-next-line no-underscore-dangle
            isDining={data[0].node.__typename === 'node__dining'}
          />
        )}
        <div className={contentClasses}>
          <Container>{children}</Container>
        </div>
        <div
          className={listClasses}
          aria-live={filters && 'polite'}
          aria-atomic={filters && 'true'}
        >
          <Container style={this.props.style}>
            {this.state.loading && (
              <Icon
                icon="loader"
                height="50"
                width="50"
                className={styles.loader}
              />
            )}

            <div className={styles.listGrid}>
              {!this.state.loading &&
                this.state.listings &&
                this.state.listings.map((item) => {
                  return <ContentTeaser key={item.node.drupal_id} data={item.node} />;
                })}
            </div>
            {/* If there are no filtered results and the filter is active let the user
            know nothing was found. */}
            {!this.state.loading &&
              this.state.filtered &&
              this.state.listings &&
              this.state.listings.length === 0 && <p>No results found.</p>}
          </Container>
        </div>
      </div>
    );
  }
}

ContentList.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  filters: PropTypes.array,
  color: PropTypes.string,
  children: PropTypes.node
};

export default ContentList;
